import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface ToolWatchFlash {
  toolName: string;
  at: string;
}

interface ToolWatchState {
  /** Contacts whose row/card should flash after a watched tool ran, keyed by contact id. */
  flashes: Record<string, ToolWatchFlash>;
  addFlash: (contactId: string, flash: ToolWatchFlash) => void;
  clearFlash: (contactId: string) => void;
  /** Merge server-side unacknowledged alerts into the store (idempotent). */
  mergeAlerts: (
    alerts: Array<{ contact_id: string; tool_name: string; created_at: string }>,
  ) => void;
}

/**
 * Tracks "watched tool invoked" highlights pushed over the messaging WebSocket.
 * A flash stays on the contact row / chat card until the user clicks it.
 * Persisted to localStorage so flashes survive page reloads.
 */
export const useToolWatchStore = create<ToolWatchState>()(
  persist(
    (set) => ({
      flashes: {},
      addFlash: (contactId, flash) =>
        set((state) => ({ flashes: { ...state.flashes, [contactId]: flash } })),
      clearFlash: (contactId) =>
        set((state) => {
          if (!state.flashes[contactId]) return state;
          const next = { ...state.flashes };
          delete next[contactId];
          return { flashes: next };
        }),
      mergeAlerts: (alerts) =>
        set((state) => {
          if (alerts.length === 0) return state;
          const merged = { ...state.flashes };
          for (const alert of alerts) {
            // Only add if not already flashing (WS may have already set it).
            if (!merged[alert.contact_id]) {
              merged[alert.contact_id] = {
                toolName: alert.tool_name,
                at: alert.created_at,
              };
            }
          }
          return { flashes: merged };
        }),
    }),
    {
      name: 'tool-watch-flashes',
      partialize: (state) => ({ flashes: state.flashes }),
    },
  ),
);
